<template>
  <div class="w-full max-w-full">
    <div :class="cn('relative overflow-auto shadow-md rounded-sm w-full', props.tableClass)">
      <table class="w-full table-auto bg-white border-black/5 text-[#213756]">
        <thead :class="cn('bg-[#F7F7F7] border-b', props.headClass)">
          <tr class="bg-inherit">
            <th
              v-for="col in props.columns"
              :class="cn('px-6 py-4 text-start', props.cellClass, props.headCellClass, col.class)"
            >
              {{ col.label ?? col.key }}
            </th>
          </tr>
        </thead>
        <tbody :class="cn('text-sm bg-inherit', props.bodyClass)">
          <tr class="bg-inherit border-b last-of-type:border-b-0" v-for="row in pagination.currentPageData">
            <td
              v-for="col in props.columns"
              :class="cn('border-[inherit] px-6 py-3', props.cellClass, props.bodyCellClass, col.class)"
            >
              <Component v-if="col.cellComponent?.(row)" :is="col.cellComponent(row)" v-bind="row" />
              <span v-else>{{ row[col.key] }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      v-if="!hidePagination"
      :class="cn('flex flex-row flex-wrap items-center gap-4 mt-4 text-sm justify-end', props.paginationClass)"
    >
      <div class="flex flex-row flex-nowrap gap-1 items-center">
        <button
          type="button"
          class="Table-Pagination__Button"
          @click="handlePaginationNavigate('first', $event)"
          :disabled="!pagination.canGoBackward"
          :title="$t('table.pagination.first')"
        >
          <ChevronDoubleLeftIcon />
        </button>
        <button
          type="button"
          class="Table-Pagination__Button"
          @click="handlePaginationNavigate('back', $event)"
          :disabled="!pagination.canGoBackward"
          :title="$t('table.pagination.previous')"
        >
          <ChevronLeftIcon />
        </button>
        <span class="font-semibold mx-3">
          {{ $t("table.pagination.page") }} {{ paginationState.page }} |
          {{ paginationState.page * paginationState.rowsPerPage }}
          {{ $t("table.pagination.of") }}
          {{ props.data.length }}
        </span>
        <button
          type="button"
          class="Table-Pagination__Button"
          @click="handlePaginationNavigate('next', $event)"
          :disabled="!pagination.canGoForward"
          :title="$t('table.pagination.next')"
        >
          <ChevronRightIcon />
        </button>
        <button
          type="button"
          class="Table-Pagination__Button"
          @click="handlePaginationNavigate('last', $event)"
          :disabled="!pagination.canGoForward"
          :title="$t('table.pagination.last')"
        >
          <ChevronDoubleRightIcon />
        </button>
      </div>
    </div>
  </div>
</template>

<script
  setup
  lang="ts"
  generic="
    TColumnDef extends Array<{
      key: keyof TData | Omit<string, keyof TData>;
      label?: string;
      class?: string;
      cellComponent?: (row: TData) => Component | null;
    }>[number],
    TData extends object
  "
>
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/vue/outline";
import type { Component } from "vue";

interface TableUIProps {
  tableClass?: string;
  headClass?: string;
  bodyClass?: string;
  cellClass?: string;
  headCellClass?: string;
  bodyCellClass?: string;
  paginationClass?: string;
}

interface TablePaginationProps {
  page: number;
  rowsPerPage: number;
}

interface TableProps extends TableUIProps {
  columns: TColumnDef[];
  data: TData[];
  pagination?: TablePaginationProps;
  hidePagination?: boolean;
}
const props = withDefaults(defineProps<TableProps>(), {
  pagination: ({ data }) => ({
    page: 1,
    rowsPerPage: data.length,
  }),
});

const paginationState = ref(props.pagination);
const pagination = computed(() => ({
  state: paginationState,
  get currentPageData() {
    const dataCopy = structuredClone(props.data);
    const startPos = (this.state.value.page - 1) * this.state.value.rowsPerPage;
    const endPos = startPos + this.state.value.rowsPerPage;
    return dataCopy.slice(startPos, endPos);
  },

  get totalPage() {
    return Math.ceil(props.data.length / this.state.value.rowsPerPage);
  },

  get canGoForward() {
    return this.state.value.page < this.totalPage;
  },

  get canGoBackward() {
    return this.state.value.page > 1;
  },
}));

function handlePaginationNavigate(action: "first" | "back" | "next" | "last", event: Event) {
  const updateField = <TField extends keyof TablePaginationProps>(field: TField, value: TablePaginationProps[TField]) =>
    (paginationState.value = {
      ...paginationState.value,
      [field]: value,
    });

  const actionHandlers: Record<typeof action, Function> = {
    first: () => updateField("page", 1),
    back: () => updateField("page", pagination.value.state.value.page - 1),
    next: () => updateField("page", pagination.value.state.value.page + 1),
    last: () => updateField("page", pagination.value.totalPage),
  };

  return actionHandlers[action](event);
}
</script>

<style scoped lang="scss">
.Table-Pagination__Button {
  @apply rounded-md border bg-white border-black/10 cursor-pointer w-7 h-7 p-1.5 shrink-0 hover:text-secondary hover:border-current disabled:opacity-50 disabled:pointer-events-none;
}
</style>
